import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — Want To Buy" },
      { name: "description", content: "Want To Buy is a connection platform for TCG collectors. Post what you want. Get connected with sellers who have it." },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <DocPage
      eyebrow="Our story"
      title="About Want To Buy"
      intro="A connection platform for trading cards — built so collectors can find each other, cut through the noise, and trade on their own terms."
    >
      <p className="!mt-0 font-display text-base font-bold uppercase tracking-[0.25em] text-primary">For Collectors, By Collectors</p>

      <section>
        <h2>What Want To Buy is</h2>
        <p>
          Want To Buy is a <strong>connection platform</strong>. Its sole purpose is to connect TCG buyers with sellers who have the specific cards those buyers are looking for.
          <strong> Post what you want. Get connected with sellers who have it.</strong>
        </p>
        <p>
          Want To Buy is <strong>not</strong> a marketplace, escrow service, payment processor, or dispute mediator. We do not hold funds, verify card authenticity, guarantee card condition, or take responsibility for transaction outcomes. Every transaction is arranged entirely between the buyer and the seller.
        </p>
      </section>

      <section>
        <h2>How the connection works</h2>
        <ol>
          <li>A buyer posts a public buy order describing the card they want and the price they're willing to pay.</li>
          <li>Sellers who physically own the card submit offers.</li>
          <li>After a buyer accepts an offer, the seller must submit <strong>2 mandatory timestamped photos</strong> — front and back of the card with a handwritten note in the same frame. This is a scam-prevention filter: it proves the seller physically possesses the card at the time of the transaction.</li>
          <li>The buyer reviews the photos, requests any additional images they want, and — if satisfied — approves. The message thread opens and the two parties arrange payment and shipping directly.</li>
        </ol>
        <p>
          Want To Buy's role ends when the two parties are connected. From that point forward, everything is between them.
        </p>
      </section>

      <section>
        <h2>The one platform rule we do enforce</h2>
        <p>
          The <strong>mandatory 2-photo timestamped requirement</strong> is the single non-negotiable rule on Want To Buy. Sellers who cannot or will not submit these two photos cannot proceed. This exists to filter out bad actors who do not physically hold the card — nothing more. Want To Buy does not authenticate, grade, or verify condition from these photos. That evaluation is the buyer's responsibility.
        </p>
      </section>

      <section>
        <h2>Payments — use PayPal Goods &amp; Services</h2>
        <p>
          Want To Buy strongly recommends that all payments be arranged through <strong>PayPal Goods &amp; Services</strong>. PayPal — not Want To Buy — provides independent buyer and seller protection through their own terms and dispute resolution process. If a transaction goes wrong, buyers should open a dispute directly with PayPal. Screenshots of the Want To Buy message thread — including the buy order, submitted photos, and all shipping communications — are accepted as evidence in PayPal disputes.
        </p>
      </section>

      <section>
        <h2>What we support</h2>
        <p>
          Pokémon, One Piece, Magic: The Gathering, Yu-Gi-Oh!, Lorcana, Digimon, and a growing list of other TCGs — raw and graded.
        </p>
      </section>

      <section className="panel-note">
        <h2 className="!mt-0">A note from the founder</h2>
        <p>Built by a collector, for collectors — because the best way to find a specific card is to have sellers come to you, not the other way around.</p>
      </section>
    </DocPage>
  );
}
